export interface UpsertSessionSongRequest {
  readonly sessionId: string;
  readonly participantId: string;
  readonly currentSongYoutubeId: string;
  readonly currentSongTitle: string;
  readonly currentSongSmamsUrl?: string;
  readonly currentSongStemUrlsByRole?: Readonly<Record<string, string>>;
}

export interface SessionSongDescriptor {
  readonly youtubeId: string;
  readonly title: string;
  readonly smamsUrl?: string;
  readonly stemUrlsByRole?: Readonly<Record<string, string>>;
}

export interface UpsertSessionSongResponse {
  readonly sessionId: string;
  readonly currentSongYoutubeId: string;
  readonly currentSongTitle: string;
  readonly currentSongSmamsUrl?: string;
  readonly currentSongStemUrlsByRole?: Readonly<Record<string, string>>;
  readonly currentSongDescriptor?: SessionSongDescriptor;
  readonly updatedAtEpochMs: number;
  readonly commandId?: string;
  readonly seq?: number;
  readonly issuerId?: string;
}

export interface SessionSongServiceOptions {
  readonly apiBaseUrl: string;
  readonly upsertPath?: string;
  readonly getAuthToken?: (() => Promise<string | undefined>) | undefined;
  readonly onUnauthorized?: (() => Promise<string | undefined>) | undefined;
  readonly fetchImpl?: typeof fetch;
}

const UPSERT_SESSION_SONG_PATH = "/v2/band-sync/livekit/session-song/upsert";

export class SessionSongService {
  private readonly apiBaseUrl: string;
  private readonly upsertPath: string;
  private readonly getAuthToken: (() => Promise<string | undefined>) | undefined;
  private readonly onUnauthorized: (() => Promise<string | undefined>) | undefined;
  private readonly fetchImpl: typeof fetch;

  constructor(options: SessionSongServiceOptions) {
    this.apiBaseUrl = options.apiBaseUrl;
    this.upsertPath = options.upsertPath ?? UPSERT_SESSION_SONG_PATH;
    this.getAuthToken = options.getAuthToken;
    this.onUnauthorized = options.onUnauthorized;
    this.fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init));
  }

  private buildEndpoint(path: string): string {
    const base = this.apiBaseUrl.trim().replace(/\/$/, "");
    return `${base}${path}`;
  }

  private async postWithAuth(
    endpoint: string,
    payload: object,
    authToken: string | undefined,
  ): Promise<Response> {
    const headers: Record<string, string> = {
      "content-type": "application/json",
    };
    if (authToken) {
      headers.authorization = `Bearer ${authToken}`;
    }
    return this.fetchImpl(endpoint, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });
  }

  async upsertSessionSong(request: UpsertSessionSongRequest): Promise<UpsertSessionSongResponse> {
    const endpoint = this.buildEndpoint(this.upsertPath);
    const initialToken = this.getAuthToken ? await this.getAuthToken() : undefined;
    let response = await this.postWithAuth(endpoint, request, initialToken);
    if (response.status === 401 && this.onUnauthorized) {
      const refreshed = await this.onUnauthorized();
      if (refreshed) {
        response = await this.postWithAuth(endpoint, request, refreshed);
      }
    }
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new Error(
        `upsert session song failed (${response.status})${body ? ` body=${body}` : ""}`,
      );
    }
    return (await response.json()) as UpsertSessionSongResponse;
  }
}
